import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight, ShieldCheck, Sparkles } from 'lucide-react';
import { FormProvider, useForm, type FieldPath, type SubmitHandler } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/cn';
import {
  createOnboardingDefaults,
  onboardingSchema,
  type OnboardingData,
  type OnboardingInitialData
} from './onboarding-schema';
import { AreasStep, GoalsStep, ProfileStep, RhythmStep, SummaryStep } from './onboarding-steps';

const STEPS = [
  {
    eyebrow: 'Deine Grundlage',
    title: 'Ein System, das zu dir passt',
    description: 'Sag uns kurz, wer du bist und worauf DISCIPLINE deinen Alltag ausrichten soll.'
  },
  {
    eyebrow: 'Dein Rhythmus',
    title: 'Gib deinem Tag einen festen Rahmen',
    description:
      'Lege einen realistischen Start und Abschluss fest. Dazwischen bleibt genug Platz für Pausen.'
  },
  {
    eyebrow: 'Deine Prioritäten',
    title: 'Was soll im Fokus stehen?',
    description: 'Wähle die Lebensbereiche, die du zuverlässig planen und verfolgen möchtest.'
  },
  {
    eyebrow: 'Dein Tagesziel',
    title: 'Setze klare, messbare Ziele',
    description:
      'Die vorbereiteten Werte sind ein guter Startpunkt. Passe sie so an, dass sie für dich erreichbar bleiben.'
  },
  {
    eyebrow: 'Alles bereit',
    title: 'Dein erster Tag kann beginnen',
    description:
      'Prüfe deine Angaben. DISCIPLINE bereitet daraus deinen persönlichen Startpunkt vor.'
  }
] as const;

const STEP_FIELDS: ReadonlyArray<ReadonlyArray<FieldPath<OnboardingData>>> = [
  ['name', 'primaryGoal'],
  ['wakeTime', 'sleepTime'],
  ['areas'],
  [
    'dailyGoals.schoolMinutes',
    'dailyGoals.frenchMinutes',
    'dailyGoals.youtubeMinutes',
    'dailyGoals.novelChapters',
    'dailyGoals.pushUpCurrentMax',
    'dailyGoals.pushUpLongTermGoal',
    'dailyGoals.squatsDaily'
  ],
  []
];

const LAST_STEP = STEPS.length - 1;

export interface OnboardingProps {
  initialData?: OnboardingInitialData;
  onComplete: (data: OnboardingData) => Promise<void>;
}

function CurrentStep({ step }: { step: number }) {
  switch (step) {
    case 0:
      return <ProfileStep />;
    case 1:
      return <RhythmStep />;
    case 2:
      return <AreasStep />;
    case 3:
      return <GoalsStep />;
    case 4:
      return <SummaryStep />;
    default:
      return null;
  }
}

function nextButtonLabel(step: number): string {
  if (step === 2) return 'Ziele festlegen';
  if (step === 3) return 'Zusammenfassung';
  return 'Weiter';
}

export function Onboarding({ initialData, onComplete }: OnboardingProps) {
  const [step, setStep] = useState(0);
  const [completionError, setCompletionError] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const defaults = useMemo(() => createOnboardingDefaults(initialData), [initialData]);
  const form = useForm<OnboardingData>({
    defaultValues: defaults,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: zodResolver(onboardingSchema)
  });
  const isSubmitting = form.formState.isSubmitting;
  const currentStep = STEPS[step];

  useEffect(() => {
    if (step > 0) headingRef.current?.focus();
  }, [step]);

  const goToNextStep = async () => {
    const fields = STEP_FIELDS[step] ?? [];
    const valid = await form.trigger(fields, { shouldFocus: true });
    if (!valid) return;

    setCompletionError(false);
    setStep((current) => Math.min(current + 1, LAST_STEP));
  };

  const goBack = () => {
    setCompletionError(false);
    setStep((current) => Math.max(current - 1, 0));
  };

  const completeOnboarding: SubmitHandler<OnboardingData> = async (data) => {
    setCompletionError(false);

    try {
      const validatedData = onboardingSchema.parse(data);
      await onComplete(validatedData);
    } catch {
      setCompletionError(true);
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    if (step < LAST_STEP) {
      event.preventDefault();
      void goToNextStep();
      return;
    }

    void form.handleSubmit(completeOnboarding)(event);
  };

  if (!currentStep) return null;

  return (
    <section
      aria-label="DISCIPLINE einrichten"
      className="surface-grid safe-top safe-bottom min-h-[100dvh] bg-canvas px-3 py-4 text-ink sm:px-6 sm:py-8"
    >
      <div className="mx-auto flex min-h-[calc(100dvh-2rem)] w-full max-w-4xl items-center sm:min-h-[calc(100dvh-4rem)]">
        <Card className="w-full overflow-hidden border-line/80 bg-surface/95 shadow-lift backdrop-blur-sm">
          <CardHeader className="block border-b border-line/75 p-5 sm:p-7">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2.5">
                <span className="flex size-9 items-center justify-center rounded-xl bg-accent text-sm font-black tracking-tight text-accent-ink shadow-soft">
                  D
                </span>
                <div>
                  <p className="font-display text-sm font-extrabold tracking-[0.14em] text-ink">
                    DISCIPLINE
                  </p>
                  <p className="text-xs text-muted">Persönliche Einrichtung</p>
                </div>
              </div>
              <div className="hidden items-center gap-2 rounded-full border border-line bg-elevated/55 px-3 py-1.5 text-xs font-medium text-muted sm:flex">
                <ShieldCheck aria-hidden="true" className="size-3.5 text-success" />
                Lokal gespeichert
              </div>
            </div>

            <div className="mt-6 flex items-center justify-between gap-4 text-xs font-semibold">
              <span className="text-accent">
                Schritt {step + 1} von {STEPS.length}
              </span>
              <span className="text-muted">{Math.round(((step + 1) / STEPS.length) * 100)} %</span>
            </div>
            <Progress
              className="mt-2.5"
              label="Onboarding-Fortschritt"
              max={STEPS.length}
              value={step + 1}
            />
            <ol aria-label="Onboarding-Schritte" className="mt-3 flex gap-1.5">
              {STEPS.map((_, index) => (
                <li
                  aria-current={index === step ? 'step' : undefined}
                  className={cn(
                    'h-1 flex-1 rounded-full transition-colors',
                    index <= step ? 'bg-accent' : 'bg-elevated'
                  )}
                  key={index}
                >
                  <span className="sr-only">
                    Schritt {index + 1}
                    {index === step ? ', aktuell' : ''}
                  </span>
                </li>
              ))}
            </ol>
          </CardHeader>

          <FormProvider {...form}>
            <form aria-busy={isSubmitting} noValidate onSubmit={handleSubmit}>
              <CardContent className="p-5 sm:p-7">
                <div aria-live="polite" className="mb-6" key={step}>
                  <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-accent">
                    {currentStep.eyebrow}
                  </p>
                  <h1
                    className="max-w-2xl font-display text-2xl font-extrabold tracking-tight text-ink outline-none sm:text-3xl"
                    ref={headingRef}
                    tabIndex={-1}
                  >
                    {currentStep.title}
                  </h1>
                  <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
                    {currentStep.description}
                  </p>
                </div>

                <CurrentStep step={step} />

                {completionError ? (
                  <div
                    className="mt-5 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3"
                    role="alert"
                  >
                    <p className="text-sm font-semibold text-danger">
                      Das Onboarding konnte nicht gespeichert werden.
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted">
                      Deine Angaben bleiben erhalten. Bitte versuche es noch einmal.
                    </p>
                  </div>
                ) : null}

                {step === LAST_STEP ? (
                  <div className="mt-5 flex items-start gap-3 rounded-xl border border-success/25 bg-success/10 p-3.5">
                    <Sparkles aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-success" />
                    <p className="text-xs leading-relaxed text-muted">
                      Nach dem Start kannst du alle Ziele, Zeiten und Bereiche jederzeit in den
                      Einstellungen anpassen.
                    </p>
                  </div>
                ) : null}
              </CardContent>

              <div className="safe-bottom flex items-center justify-between gap-3 border-t border-line/75 bg-surface/95 p-4 sm:px-7 sm:py-5">
                {step > 0 ? (
                  <Button disabled={isSubmitting} onClick={goBack} type="button" variant="ghost">
                    <ArrowLeft aria-hidden="true" className="size-4" />
                    Zurück
                  </Button>
                ) : (
                  <span aria-hidden="true" />
                )}

                {step < LAST_STEP ? (
                  <Button type="submit">
                    {nextButtonLabel(step)}
                    <ArrowRight aria-hidden="true" className="size-4" />
                  </Button>
                ) : (
                  <Button loading={isSubmitting} size="lg" type="submit">
                    {isSubmitting ? 'App wird vorbereitet …' : 'App starten'}
                    {!isSubmitting ? <ArrowRight aria-hidden="true" className="size-4" /> : null}
                  </Button>
                )}
              </div>
            </form>
          </FormProvider>
        </Card>
      </div>
    </section>
  );
}
